/**
 * Phone detection, kept pure so the decision is unit-testable in node (the
 * frontend suite has no DOM harness). `useIsPhone` in hooks/use-is-phone.ts
 * feeds it the live viewport; sgp.css mirrors the SAME predicate as a media
 * query (`(pointer: coarse) and (max-width: 550px)` OR `... (max-height:
 * 550px)`, which is exactly "shorter side <= 550"), so a layout decision made
 * here and a touch-target rule made there agree on what a phone is.
 *
 * Deliberately NOT user-agent sniffing: those strings are unreliable (iPadOS
 * has reported itself as a Mac since 13) and rot over time. The browser
 * answers two questions accurately instead -- is the pointer coarse, and how
 * long is the SHORTER viewport side. Using the shorter side rather than the
 * width makes the answer rotation-proof: a phone in landscape is 900px wide
 * but still ~400px tall, while an iPad in landscape is 768px+ either way.
 *
 * Both consequences are intentional: a desktop user who narrows their window
 * keeps the full app (fine pointer), and a tablet keeps the Matrix and
 * Timeline in either orientation -- they are genuinely usable at that size.
 */

/** px -- comfortably above any phone's short side, below any tablet's. */
export const PHONE_SHORT_SIDE = 550;

export interface PhoneViewport {
  /** `(pointer: coarse)` matches -- touch, not a mouse. */
  coarse: boolean;
  /** window.innerWidth / innerHeight. */
  w: number;
  h: number;
  /** `?phone=1|0` URL override; `null` when absent. Wins over detection. */
  override: boolean | null;
}

export function isPhoneViewport({ coarse, w, h, override }: PhoneViewport): boolean {
  if (override !== null) return override;
  const shortSide = Math.min(w, h);
  // shortSide > 0 guards the zero-size window a headless/pre-layout pass can
  // report, which would otherwise read as "phone" on every coarse device.
  return coarse && shortSide > 0 && shortSide <= PHONE_SHORT_SIDE;
}

/** `?phone=1` forces the phone layout, `?phone=0` the full app. Lets either
 * layout be checked from a laptop without a handset, and is the escape hatch
 * if a device is ever misdetected in the field. Anything else -> `null`. */
export function parsePhoneOverride(search: string): boolean | null {
  const value = new URLSearchParams(search).get("phone");
  if (value === "1" || value === "true") return true;
  if (value === "0" || value === "false") return false;
  return null;
}
